/**
 * Mock data pour la Matériauthèque IA — extraction CCTP.
 *
 * Structure prête à être remplacée par un vrai endpoint backend
 * (par ex. POST /api/materialtheque/extract → return { sections: [...] }).
 *
 * Chaque matériau a un `confidence` (0-1) qui pilote le badge :
 *   - >= 0.90 → "Fiable" (emerald)
 *   - >= 0.80 → "Bonne"  (gold)
 *   - <  0.80 → "À vérifier" (warning) + glow sur la card
 */

export const EXTRACTION_PROGRESS_STEPS = [
  { key: 'parse',    label: 'Analyse du CCTP...',          progress: 22 },
  { key: 'extract',  label: 'Extraction des revêtements...', progress: 58 },
  { key: 'thumbs',   label: 'Génération des miniatures...',  progress: 86 },
  { key: 'done',     label: 'Validation finale...',          progress: 100 },
];

/** Types de pièces (miroir de ROOM_TYPES backend — materialtheque_photos.py). */
export const ROOM_TYPES = [
  { id: 'sejour', label: 'Séjour / Salon', icon: 'Sofa' },
  { id: 'cuisine', label: 'Cuisine', icon: 'UtensilsCrossed' },
  { id: 'chambre', label: 'Chambre', icon: 'BedDouble' },
  { id: 'salle_de_bain', label: 'Salle de bain', icon: 'Bath' },
  { id: 'wc', label: 'WC', icon: 'Bath' },
  { id: 'entree', label: 'Entrée / Dégagement', icon: 'DoorOpen' },
  { id: 'bureau', label: 'Bureau', icon: 'Briefcase' },
  { id: 'terrasse_balcon', label: 'Terrasse / Balcon', icon: 'Trees' },
  { id: 'buanderie', label: 'Buanderie / Cellier', icon: 'Boxes' },
  { id: 'autre', label: 'Autre pièce', icon: 'LayoutGrid' },
];

const SAMPLE_PDF_NAME = 'CCTP_Residence_Menara_v3.pdf';
const SAMPLE_PROJECT  = 'Résidence Menara — Lot Sud';

export const MATERIALTHEQUE_MOCK = {
  source: {
    project: SAMPLE_PROJECT,
    file: SAMPLE_PDF_NAME,
    pages: 142,
    extracted_at: '2026-02-08T14:32:00Z',
  },
  stats: {
    total: 14,
    high_confidence: 9,
    needs_review: 3,
    low_confidence: 2,
  },
  sections: [
    {
      id: 'sols-pieces-vie',
      label: 'Sols pièces de vie',
      icon: 'LayoutGrid',
      items: [
        {
          id: 'mat-001',
          name: 'Parquet Montana Roble 60×60',
          ref: 'BAT-PARQ-MONT-RBL',
          category: 'Sol',
          finish: 'Mat huilé',
          dimensions: '600 × 600 mm',
          confidence: 0.96,
          source_page: 18,
          thumb: null,
          thumb_color: '#7B5B3F',
          thumb_pattern: 'wood',
        },
        {
          id: 'mat-002',
          name: 'Carrelage Grès Cérame Travertin Cream',
          ref: 'BAT-CARR-TRV-CRM',
          category: 'Sol',
          finish: 'Mat structuré',
          dimensions: '600 × 1200 mm',
          confidence: 0.91,
          source_page: 22,
          thumb: null,
          thumb_color: '#C8B58B',
          thumb_pattern: 'stone',
        },
        {
          id: 'mat-003',
          name: 'Plinthe assortie travertin H80',
          ref: 'BAT-PLNT-TRV-80',
          category: 'Plinthe',
          finish: 'Mat',
          dimensions: '80 × 600 mm',
          confidence: 0.74,
          source_page: 23,
          thumb: null,
          thumb_color: '#B8A375',
          thumb_pattern: 'stone',
        },
      ],
    },
    {
      id: 'sols-chambres',
      label: 'Sols chambres',
      icon: 'BedDouble',
      items: [
        {
          id: 'mat-004',
          name: 'Moquette laine boucle Olivia Greige',
          ref: 'BAT-MOQ-OLV-GRG',
          category: 'Sol',
          finish: 'Boucle',
          dimensions: 'Rouleau 4 m',
          confidence: 0.88,
          source_page: 31,
          thumb: null,
          thumb_color: '#A8A19B',
          thumb_pattern: 'fabric',
        },
        {
          id: 'mat-005',
          name: 'Parquet contrecollé Chêne Blanchi',
          ref: 'BAT-PARQ-CHN-BLC',
          category: 'Sol',
          finish: 'Verni mat',
          dimensions: '190 × 1900 mm',
          confidence: 0.94,
          source_page: 33,
          thumb: null,
          thumb_color: '#D6C9B0',
          thumb_pattern: 'wood',
        },
      ],
    },
    {
      id: 'salles-bain',
      label: 'Salles de bain',
      icon: 'Bath',
      items: [
        {
          id: 'mat-006',
          name: 'Faïence Zellige Bleu Atlas brillant',
          ref: 'BAT-FAI-ZLG-BL',
          category: 'Mur',
          finish: 'Brillant',
          dimensions: '100 × 100 mm',
          confidence: 0.97,
          source_page: 47,
          thumb: null,
          thumb_color: '#1F4F7A',
          thumb_pattern: 'tile',
        },
        {
          id: 'mat-007',
          name: 'Sol grès cérame anti-dérapant Pierre claire',
          ref: 'BAT-SOL-GR-PR',
          category: 'Sol',
          finish: 'Anti-dérapant R10',
          dimensions: '300 × 300 mm',
          confidence: 0.92,
          source_page: 48,
          thumb: null,
          thumb_color: '#BFB7AB',
          thumb_pattern: 'stone',
        },
        {
          id: 'mat-008',
          name: 'Vasque encastrée céramique blanche',
          ref: 'BAT-SAN-VS-WHT',
          category: 'Sanitaire',
          finish: 'Blanc satiné',
          dimensions: '600 × 400 mm',
          confidence: 0.69,
          source_page: 51,
          thumb: null,
          thumb_color: '#F0EFEB',
          thumb_pattern: 'flat',
        },
      ],
    },
    {
      id: 'cuisines',
      label: 'Cuisines',
      icon: 'UtensilsCrossed',
      items: [
        {
          id: 'mat-009',
          name: 'Plan de travail Quartz Calacatta blanc',
          ref: 'BAT-PT-QTZ-CAL',
          category: 'Plan de travail',
          finish: 'Poli mat',
          dimensions: '20 mm épaisseur',
          confidence: 0.95,
          source_page: 62,
          thumb: null,
          thumb_color: '#E8E5E0',
          thumb_pattern: 'stone',
        },
        {
          id: 'mat-010',
          name: 'Crédence verre laqué taupe',
          ref: 'BAT-CRD-VRR-TPE',
          category: 'Crédence',
          finish: 'Brillant',
          dimensions: '6 mm',
          confidence: 0.81,
          source_page: 63,
          thumb: null,
          thumb_color: '#7E7268',
          thumb_pattern: 'flat',
        },
      ],
    },
    {
      id: 'exterieurs',
      label: 'Extérieurs',
      icon: 'Trees',
      items: [
        {
          id: 'mat-011',
          name: 'Terrasse bois exotique Ipé huilé',
          ref: 'BAT-TRS-IPE-HUI',
          category: 'Terrasse',
          finish: 'Huilé',
          dimensions: '21 × 145 × 2200 mm',
          confidence: 0.93,
          source_page: 78,
          thumb: null,
          thumb_color: '#5C3F26',
          thumb_pattern: 'wood',
        },
        {
          id: 'mat-012',
          name: 'Dalles pierre reconstituée Beige Sahara',
          ref: 'BAT-DAL-PR-SHR',
          category: 'Sol extérieur',
          finish: 'Vieillie',
          dimensions: '600 × 600 × 30 mm',
          confidence: 0.86,
          source_page: 80,
          thumb: null,
          thumb_color: '#C5B698',
          thumb_pattern: 'stone',
        },
        {
          id: 'mat-013',
          name: 'Bardage façade pin Douglas naturel',
          ref: 'BAT-BRD-DGL-NAT',
          category: 'Façade',
          finish: 'Brut autoclave',
          dimensions: '21 × 130 mm',
          confidence: 0.78,
          source_page: 84,
          thumb: null,
          thumb_color: '#9B7A55',
          thumb_pattern: 'wood',
        },
        {
          id: 'mat-014',
          name: 'Garde-corps inox brossé H110',
          ref: 'BAT-GC-INX-110',
          category: 'Garde-corps',
          finish: 'Brossé',
          dimensions: 'H 1100 mm',
          confidence: 0.65,
          source_page: 86,
          thumb: null,
          thumb_color: '#B8B8B8',
          thumb_pattern: 'flat',
        },
      ],
    },
  ],
};

/**
 * Helper : tone du badge selon le score IA.
 */
export const getConfidenceTone = (c) => {
  if (c >= 0.90) return { tone: 'success', label: 'Fiable',     glow: false };
  if (c >= 0.80) return { tone: 'gold',    label: 'Bonne',      glow: false };
  return                  { tone: 'warning', label: 'À vérifier', glow: true  };
};

/**
 * Helper : pourcentage humain.
 */
export const formatConfidence = (c) => `${Math.round(c * 100)}%`;
